import * as ChannelService from "@channel.io/channel-web-sdk-loader";
import classNames from "classnames";
import React, { useEffect, useMemo, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import useCartInfo from "@repo/shopify/hooks/useCartInfo";
import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";

import Button from "@atoms/Button";
import Price from "@atoms/Price";

import CartLineItem from "@molecules/CartLineItem";

import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import useCartQuery from "@hooks/useCartQuery";
import useCheckLocation from "@hooks/useCheckLocation";
import useCheckPurchaseLimit from "@hooks/useCheckPurchaseLimit";
import useLanguage from "@hooks/useLanguage";

import { useCartStore } from "@store/cart";
import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

export default function HeaderCart() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { t } = useTranslation();
  const { isPC } = useCheckDevice();
  const { lang } = useLanguage();
  const { isDanielJohnston } = useCheckLocation();

  const isJp = lang === "jp";

  const { token, caaToken, customer, caaCustomer } = useCustomerStore();
  const hasCustomerSession = isJp ? Boolean(caaToken?.accessToken) : Boolean(token.accessToken);
  const { cart, isSyncing } = useCartStore();
  const { openCart, openToast, openAccount, openModal } = useLayoutStore();
  const { updateCartLine, removeCartLine, updateCartLineLoading, removeCartLineLoading, addCartLineLoading } = useCartQuery();

  const { remainingLimit, expectedRemainingLimit } = useCheckPurchaseLimit();

  const ref = useRef<HTMLDivElement | null>(null);

  const { totalQuantity, subTotalAmount } = useCartInfo(cart);

  // 카트 수량 변경, 사은품 처리, 로그인 시 카트 복원/병합 중인지 확인
  const isCartUpdating = useMemo(
    () => updateCartLineLoading || removeCartLineLoading || addCartLineLoading || isSyncing,
    [updateCartLineLoading, removeCartLineLoading, addCartLineLoading, isSyncing],
  );

  useEffect(() => {
    ChannelService.track("openCart");
  }, []);

  const handleCheckout = () => {
    sessionStorage.setItem("checkoutBackUrl", pathname);

    if (!hasCustomerSession) {
      openToast(NOTIFICATIONS.WARNING_NO_LOGIN);
      openCart(false);
      openAccount(true);
      sessionStorage.setItem("loginAfterUrl", URLS.CHECKOUT);
      return;
    }

    if ((isJp ? caaCustomer?.tags : customer.tags)?.includes("PARTNERSHIP_TOSS")) {
      if (remainingLimit <= 0 || expectedRemainingLimit < 0) {
        openModal({
          title: "WARNING",
          content: (
            <p>
              {t("warning.exceed_purchase_limit")}
              <br />
              <Trans i18nKey="warning.remaining_limit" values={{ amount: remainingLimit.toLocaleString() }} />
            </p>
          ),
          confirm: {
            label: "Okay",
            onClick: () => {
              openModal(null);
            },
          },
        });

        return;
      }
    }

    openCart(false);

    // JP는 커스텀 체크아웃 대신 Shopify 기본 체크아웃으로 바로 이동
    if (isJp) {
      if (cart.checkoutUrl) window.location.href = cart.checkoutUrl;
      return;
    }

    navigate(URLS.CHECKOUT);
  };

  return (
    <div
      className={classNames(
        "pc:right-0 pc:h-dvh pc:max-h-dvh pc:w-96 pc:pt-4 pc:p-5 pc:z-60 fixed z-30 flex h-dvh w-full flex-col gap-2.5 bg-white p-4 pt-0 lg:w-1/4 dark:text-white",
        !isPC ? [cart?.lines.edges.some(({ node }) => node.quantity > 0) ? "top-0" : "top-13.5"] : "top-0",
        isDanielJohnston ? "dark:bg-dj-black" : "dark:bg-black",
      )}
    >
      {isPC ? (
        <div className="flex w-full justify-between">
          <span>{totalQuantity > 0 ? `${totalQuantity} Items` : "Empty Cart"}</span>
          <Button
            as="text"
            onClick={() => {
              openCart(false);
            }}
          >
            Close
          </Button>
        </div>
      ) : null}

      <div className="flex flex-col gap-2.5">
        {cart.lines.edges.some(({ node }) => node.quantity > 0) ? (
          <>
            <div className="scrollbar-hide pc:pt-4 flex h-[calc(100dvh-11rem)] flex-col gap-5 overflow-y-scroll pt-17.5">
              {cart.lines.edges
                ?.filter(({ node }) => !node.merchandise.product.tags.includes("Gift") && node.quantity > 0)
                .map(({ node }) => (
                  <CartLineItem
                    key={node.id}
                    node={node}
                    removeCartLine={removeCartLine}
                    removeCartLineLoading={removeCartLineLoading || isSyncing}
                    updateCartLine={updateCartLine}
                    updateCartLineLoading={updateCartLineLoading || isSyncing}
                  />
                ))}
              {cart.lines.edges
                ?.filter(({ node }) => node.merchandise.product.tags.includes("Gift") && node.quantity > 0)
                .map(({ node }) => (
                  <CartLineItem
                    key={node.id}
                    node={node}
                    removeCartLine={removeCartLine}
                    removeCartLineLoading={removeCartLineLoading || isSyncing}
                    updateCartLine={updateCartLine}
                    updateCartLineLoading={updateCartLineLoading || isSyncing}
                  />
                ))}
            </div>

            <div
              className={classNames(
                "pc:p-4.5 absolute bottom-0 left-0 z-20 flex w-full flex-col gap-5 bg-white p-4 px-4",
                isDanielJohnston ? "dark:bg-dj-black" : "dark:bg-black",
              )}
              ref={ref}
            >
              <div className="flex gap-2.5">
                <span className="text-right">Subtotal</span>
                <div className="flex w-full flex-col gap-1">
                  <Price price={subTotalAmount} />
                  <p className="text-sm font-normal">{t("cart.discount")}</p>
                </div>
              </div>
              <Button as="square" className="w-full" disabled={isCartUpdating} onClick={handleCheckout}>
                Checkout Now
              </Button>
            </div>
          </>
        ) : (
          <div
            className={classNames(
              "pc:h-[calc(100dvh-4rem)] flex h-[calc(100dvh-4.25rem)] flex-col justify-between gap-4",
              isDanielJohnston ? "dark:bg-dj-black" : "dark:bg-black",
            )}
          >
            <span>No items in your cart</span>
            <Button
              as="square"
              className="w-full"
              onClick={() => {
                openCart(false);
              }}
            >
              Continue Shopping
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
